import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type TooltipContentProps,
} from "recharts";
import type {
  InsightsProductUsagePageRow,
  InsightsProductUsageRoleFilter,
} from "@shared-core/data/api-types";
import {
  ROLE_FILTER_LABELS,
  sortPagesByTotal,
} from "@shared-core/domain/insights-product-usage";
import { niceAxisMax } from "@shared-core/domain/insights-monthly-revenue";

// "Pages opened" — the most informative section of the static reports
// this tab replaces (spec §8 point 3): page views per route, split by role
// bucket, narrowed to the staff filter selected above (a filtered request
// zeroes the non-selected columns and drops routes with nothing left — the
// server does this in SQL). A horizontal grouped bar so route labels
// (often a full path) stay legible — this section is deliberately given
// the most vertical room on the tab.
//
// Under a single-role filter only one column ever carries data, so the
// legend/tooltip/subtitle show only that series rather than declaring
// three when two of them are guaranteed empty — the same "don't show an
// empty thing next to a populated one" concern as the tiles.

type RoleBucket = "sales_rep" | "csr" | "wider_team";

interface SeriesSpec {
  bucket: RoleBucket;
  dataKey: "sales_rep_views" | "csr_views" | "wider_team_views";
  color: string;
}

const SALES_REP_COLOR = "var(--color-chart-1)";
const CSR_COLOR = "var(--color-chart-2)";
const WIDER_TEAM_COLOR = "var(--color-muted-foreground)";

const SERIES: SeriesSpec[] = [
  { bucket: "sales_rep", dataKey: "sales_rep_views", color: SALES_REP_COLOR },
  { bucket: "csr", dataKey: "csr_views", color: CSR_COLOR },
  {
    bucket: "wider_team",
    dataKey: "wider_team_views",
    color: WIDER_TEAM_COLOR,
  },
];

export interface ProductUsagePagesChartProps {
  pages: InsightsProductUsagePageRow[];
  /** The staff filter currently applied to the tab — narrows the legend/tooltip/subtitle to the one series that can carry data. Defaults to "all" (every series shown). */
  role?: InsightsProductUsageRoleFilter;
}

export function ProductUsagePagesChart({
  pages,
  role = "all",
}: ProductUsagePagesChartProps) {
  const data = sortPagesByTotal(pages);

  if (data.length === 0) {
    return (
      <div className="border-border bg-surface text-muted-foreground rounded-lg border p-12 text-center text-sm">
        No page-view data for this window yet.
      </div>
    );
  }

  const visibleSeries =
    role === "all" ? SERIES : SERIES.filter((s) => s.bucket === role);
  const subtitle =
    role === "all"
      ? "Page views per route, split by role"
      : "Page views per route";

  const valueMax = niceAxisMax(
    Math.max(
      0,
      ...data.map((d) =>
        Math.max(d.sales_rep_views, d.csr_views, d.wider_team_views),
      ),
    ),
  );
  // Give each route row real room — the point of this section is that the
  // route labels and the role split are both legible at a glance.
  const chartHeight = Math.max(280, data.length * 42);

  return (
    <div className="space-y-3">
      <div>
        <h2 className="text-foreground text-base font-semibold">
          Pages opened
        </h2>
        <p className="text-muted-foreground text-sm">{subtitle}</p>
      </div>

      <div className="border-border bg-surface rounded-lg border p-4">
        <ChartLegend series={visibleSeries} />
        <ResponsiveContainer width="100%" height={chartHeight}>
          <BarChart
            data={data}
            layout="vertical"
            margin={{ top: 4, right: 16, bottom: 4, left: 4 }}
            barGap={2}
          >
            <CartesianGrid stroke="var(--color-border)" horizontal={false} />
            <XAxis
              type="number"
              domain={[0, valueMax]}
              allowDataOverflow
              tick={{ fontSize: 10, fill: "var(--color-muted-foreground)" }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              type="category"
              dataKey="route"
              width={160}
              tick={{ fontSize: 11, fill: "var(--color-foreground)" }}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip
              cursor={{ fill: "var(--color-muted)", opacity: 0.3 }}
              content={
                ((props: TooltipContentProps<number, string>) => (
                  <PagesTooltip {...props} series={visibleSeries} />
                )) as never
              }
            />
            <Bar
              dataKey="sales_rep_views"
              name={ROLE_FILTER_LABELS.sales_rep}
              fill={SALES_REP_COLOR}
              radius={[0, 2, 2, 0]}
              isAnimationActive={false}
              fillOpacity={0.95}
            />
            <Bar
              dataKey="csr_views"
              name={ROLE_FILTER_LABELS.csr}
              fill={CSR_COLOR}
              radius={[0, 2, 2, 0]}
              isAnimationActive={false}
              fillOpacity={0.95}
            />
            <Bar
              dataKey="wider_team_views"
              name={ROLE_FILTER_LABELS.wider_team}
              fill={WIDER_TEAM_COLOR}
              radius={[0, 2, 2, 0]}
              isAnimationActive={false}
              fillOpacity={0.7}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function ChartLegend({ series }: { series: SeriesSpec[] }) {
  return (
    <div className="text-muted-foreground mb-3 flex flex-wrap items-center gap-4 text-xs">
      {series.map((s) => (
        <LegendSwatch
          key={s.bucket}
          color={s.color}
          label={ROLE_FILTER_LABELS[s.bucket]}
        />
      ))}
    </div>
  );
}

function LegendSwatch({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        className="inline-block h-3 w-3 rounded-sm"
        style={{ backgroundColor: color }}
      />
      {label}
    </span>
  );
}

function PagesTooltip(
  props: TooltipContentProps<number, string> & { series: SeriesSpec[] },
) {
  if (!props.active || !props.payload || props.payload.length === 0) {
    return null;
  }
  const datum = props.payload[0]?.payload as
    InsightsProductUsagePageRow | undefined;
  if (!datum) return null;
  return (
    <div
      className="border-border bg-popover text-popover-foreground rounded-lg border px-3 py-2 text-xs shadow-lg"
      role="tooltip"
    >
      <div className="mb-1 font-semibold">{datum.route}</div>
      {props.series.map((s) => (
        <TooltipRow
          key={s.bucket}
          color={s.color}
          label={ROLE_FILTER_LABELS[s.bucket]}
          value={datum[s.dataKey]}
        />
      ))}
      <div className="text-muted-foreground mt-0.5">
        {datum.total_views} views total
      </div>
    </div>
  );
}

function TooltipRow({
  color,
  label,
  value,
}: {
  color: string;
  label: string;
  value: number;
}) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="inline-flex items-center gap-1.5">
        <span
          className="inline-block h-2 w-2 rounded-sm"
          style={{ backgroundColor: color }}
        />
        {label}
      </span>
      <span className="font-mono tabular-nums">{value}</span>
    </div>
  );
}
